// Lightweight WebAudio SFX kit for SyncDuel — no external assets
// Usage: sfx.prepare() on mount, sfx.unlock() on first user gesture

class GoofySfx {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private unlocked = false;
  private muted = false;
  private lastTickSec = -1;

  constructor() {
    try { this.muted = localStorage.getItem('sfxMuted') === '1'; } catch {}
  }

  prepare() {
    if (this.ctx) return;
    const AC: typeof AudioContext | undefined = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const master = ctx.createGain();
    master.gain.value = this.muted ? 0 : 0.75;
    master.connect(ctx.destination);
    this.ctx = ctx;
    this.master = master;
  }

  async unlock() {
    if (!this.ctx) this.prepare();
    if (!this.ctx || this.unlocked) return;
    try {
      await this.ctx.resume();
      this.unlocked = true;
      // tiny ping to ensure iOS unlock
      this.tone(880, 0.03, { gain: 0.0005 });
    } catch {}
  }

  setMuted(m: boolean) {
    this.muted = m;
    try { localStorage.setItem('sfxMuted', m ? '1' : '0'); } catch {}
    if (this.master) this.master.gain.value = m ? 0 : 0.75;
  }
  toggleMute() { this.setMuted(!this.muted); }
  isMuted() { return this.muted; }

  private tone(freq: number, dur = 0.12, opts?: { type?: OscillatorType; gain?: number; attack?: number; release?: number; glideTo?: number; glideMs?: number }) {
    if (!this.ctx || !this.master) return;
    const t0 = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    osc.type = opts?.type || 'sine';
    const g = this.ctx.createGain();
    g.gain.value = 0;
    g.connect(this.master);
    osc.connect(g);

    const attack = Math.max(0.001, opts?.attack ?? 0.004);
    const release = Math.max(0.001, opts?.release ?? 0.08);
    const peak = opts?.gain ?? 0.25;

    g.gain.cancelScheduledValues(t0);
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(peak, t0 + attack);
    g.gain.linearRampToValueAtTime(0, t0 + Math.max(0.001, dur - release));
    g.gain.linearRampToValueAtTime(0, t0 + dur);

    osc.frequency.setValueAtTime(Math.max(10, freq), t0);
    if (opts?.glideTo && opts?.glideMs) {
      osc.frequency.exponentialRampToValueAtTime(Math.max(10, opts.glideTo), t0 + opts.glideMs / 1000);
    }

    osc.start(t0);
    osc.stop(t0 + dur + 0.02);
  }

  private noiseBurst(dur = 0.12, opts?: { gain?: number; lp?: number; hp?: number }) {
    if (!this.ctx || !this.master) return;
    const t0 = this.ctx.currentTime;
    const bufferSize = Math.ceil(this.ctx.sampleRate * dur);
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1; // white noise

    const src = this.ctx.createBufferSource();
    src.buffer = buffer;

    const g = this.ctx.createGain();
    g.gain.value = opts?.gain ?? 0.15;

    const hp = this.ctx.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = opts?.hp ?? 700;
    const lp = this.ctx.createBiquadFilter(); lp.type = 'lowpass';  lp.frequency.value = opts?.lp ?? 6000;

    src.connect(hp); hp.connect(lp); lp.connect(g); g.connect(this.master);

    const attack = 0.005, release = 0.08;
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(opts?.gain ?? 0.15, t0 + attack);
    g.gain.linearRampToValueAtTime(0, t0 + Math.max(0.02, dur - release));

    src.start(t0);
    src.stop(t0 + dur + 0.02);
  }

  // Events
  hover() { this.tone(520, 0.06, { type: 'triangle', gain: 0.12 }); }
  click() { this.tone(360, 0.06, { type: 'square', gain: 0.2 }); }
  startRound() {
    this.tone(660, 0.06, { type: 'triangle', gain: 0.18 });
    setTimeout(() => this.tone(760, 0.06, { type: 'triangle', gain: 0.16 }), 80);
    setTimeout(() => this.tone(900, 0.08, { type: 'square', gain: 0.2 }), 160);
  }
  tick(secLeft: number) {
    if (!Number.isFinite(secLeft)) return;
    const whole = Math.floor(secLeft);
    if (whole === this.lastTickSec) return;
    this.lastTickSec = whole;
    if (whole <= 0) return;
    const urgent = whole <= 3;
    this.tone(urgent ? 940 : 740, urgent ? 0.05 : 0.045, { type: urgent ? 'square' as OscillatorType : 'triangle', gain: urgent ? 0.24 : 0.16 });
  }
  correct(opts?: { fastest?: boolean; big?: boolean }) {
    this.tone(660, 0.07, { type: 'sine', gain: 0.2 });
    setTimeout(() => this.tone(880, 0.08, { type: 'sine', gain: 0.18 }), 70);
    setTimeout(() => this.tone(1100, 0.1, { type: 'triangle', gain: 0.16 }), 140);
    if (opts?.fastest) this.zap();
    if (opts?.big) setTimeout(() => this.tone(1320, 0.12, { type: 'triangle', gain: 0.18 }), 210);
  }
  wrong() { this.tone(220, 0.18, { type: 'sawtooth', gain: 0.22, glideTo: 120, glideMs: 160 }); }
  zap() { this.noiseBurst(0.09, { gain: 0.22, lp: 5500, hp: 900 }); }
  points(amount: number) {
    const amt = Math.max(1, Math.min(30, amount));
    const base = 520 + amt * 12;
    this.tone(base, 0.08, { type: 'square', gain: 0.16 });
    setTimeout(() => this.tone(base + 120, 0.06, { type: 'triangle', gain: 0.14 }), 70);
  }
  readyOn()  { this.tone(720, 0.08, { type: 'triangle', gain: 0.18 }); }
  readyOff() { this.tone(480, 0.08, { type: 'triangle', gain: 0.18 }); }
  join()     { this.tone(600, 0.06, { type: 'sine', gain: 0.14 }); }
  leave()    { this.tone(300, 0.06, { type: 'sine', gain: 0.14 }); }
  win() {
    this.tone(660, 0.09, { type: 'triangle', gain: 0.22 });
    setTimeout(() => this.tone(880, 0.1, { type: 'triangle', gain: 0.2 }), 100);
    setTimeout(() => this.tone(1320, 0.12, { type: 'square', gain: 0.22 }), 220);
  }
  lose() { this.tone(440, 0.12, { type: 'sawtooth', gain: 0.2, glideTo: 220, glideMs: 180 }); }
}

export const sfx = new GoofySfx();
export default sfx;
